/**
 * Australian-specific formatters for display and input formatting
 */

/**
 * Formats Australian phone numbers for display
 * Mobile: 0400 000 000
 * Landline: (02) 0000 0000
 */
export const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/[\s\-\(\)]/g, '');

  // Handle mobile numbers
  if (cleaned.startsWith('04') || cleaned.startsWith('614')) {
    const digits = cleaned.replace(/^\+?61/, '0');
    return digits.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3');
  }

  // Handle landline numbers
  if (cleaned.match(/^(?:\+?61|0)?[2-9]\d{8}$/)) {
    const digits = cleaned.replace(/^\+?61/, '0');
    return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2 $3');
  }

  return phone;
};

/**
 * Formats BSB for display (000-000)
 */
export const formatBSB = (bsb: string): string => {
  const cleaned = bsb.replace(/[\s\-]/g, '');
  return cleaned.replace(/(\d{3})(\d{3})/, '$1-$2');
};

/**
 * Formats Australian postcode
 */
export const formatPostcode = (postcode: string): string => {
  return postcode.replace(/\D/g, '').slice(0, 4);
};

/**
 * Formats a value as Australian dollars.
 *
 * Single canonical currency formatter for the app. Non-finite input
 * (null, undefined, NaN) returns the `fallback` string — defaults to '—'
 * because that is the dominant convention for "unavailable" values across
 * the codebase. Callers that need a different fallback (e.g. '$0') can
 * override it via the option.
 */
export const formatCurrency = (
  value: number | null | undefined,
  options?: {
    maximumFractionDigits?: number;
    minimumFractionDigits?: number;
    fallback?: string;
  }
): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return options?.fallback ?? '—';
  }
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: options?.minimumFractionDigits,
    maximumFractionDigits: options?.maximumFractionDigits
  }).format(value);
};

/**
 * Formats a value as Australian dollars, prefixing a minus sign for
 * negative values. Used for debt-style displays where the sign matters.
 * Same fallback semantics as `formatCurrency`.
 */
export const formatCurrencyWithSign = (
  value: number | null | undefined,
  options?: {
    maximumFractionDigits?: number;
    minimumFractionDigits?: number;
    fallback?: string;
  }
): string => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return options?.fallback ?? '—';
  }
  const formatted = formatCurrency(Math.abs(value), options);
  return value < 0 ? `-${formatted}` : formatted;
};

/**
 * Formats ABN with spaces (00 000 000 000)
 */
export const formatABN = (abn: string): string => {
  const cleaned = abn.replace(/\s/g, '');
  return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{3})/, '$1 $2 $3 $4');
};

/**
 * Formats ACN with spaces (000 000 000)
 */
export const formatACN = (acn: string): string => {
  const cleaned = acn.replace(/\s/g, '');
  return cleaned.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3');
};

/**
 * Formats TFN with spaces (000 000 000)
 */
export const formatTFN = (tfn: string): string => {
  const cleaned = tfn.replace(/\s/g, '');
  if (cleaned.length === 8) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{2})/, '$1 $2 $3');
  }
  return cleaned.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3');
};

/**
 * Formats bank account number
 */
export const formatAccountNumber = (accountNumber: string): string => {
  const cleaned = accountNumber.replace(/[\s\-]/g, '');
  // Format in groups of 3 for readability
  return cleaned.replace(/(\d{3})(?=\d)/g, '$1 ').trim();
};

/**
 * Formats date to Australian format (DD/MM/YYYY)
 */
export const formatDateAU = (date: string | Date): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Parses Australian date format (DD/MM/YYYY) to ISO format (YYYY-MM-DD)
 */
export const parseAUDateToISO = (dateStr: string): string => {
  const parts = dateStr.split('/');
  if (parts.length !== 3) return dateStr;

  const [day, month, year] = parts;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

/**
 * Formats a percentage value
 */
export const formatPercentage = (
  value: number,
  decimals: number = 2
): string => {
  return `${value.toFixed(decimals)}%`;
};

/**
 * Collection of all Australian formatters for easy import
 */
export const australianFormatters = {
  phone: formatPhoneNumber,
  bsb: formatBSB,
  postcode: formatPostcode,
  currency: formatCurrency,
  currencyWithSign: formatCurrencyWithSign,
  abn: formatABN,
  acn: formatACN,
  tfn: formatTFN,
  accountNumber: formatAccountNumber,
  date: formatDateAU,
  parseDate: parseAUDateToISO,
  percentage: formatPercentage
};
